import { useDiaryInvalidation } from "@/features/diaries/api/diaryInvalidation";
import { supabaseClient } from "@/lib/supabase";
import type { Education } from "@/types/database/events";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";

export async function updateEducation(
	educationData: Education["Update"] & { education_id: string },
) {
	const { data, error } = await supabaseClient
		.from("educations")
		.update(educationData)
		.eq("education_id", educationData.education_id)
		.select()
		.single();
	if (error) throw error;
	return data;
}

interface EducationMutation {
	diaryId: string;
	educationId: string;
}

type UpdateEducationResponse = Awaited<ReturnType<typeof updateEducation>>;

export const useUpdateEducationMutation = ({
	diaryId,
	educationId,
}: EducationMutation) => {
	const { invalidateAll, invalidateById } = useDiaryInvalidation();

	return useMutation({
		mutationKey: ["update-education", { education_id: educationId }],
		mutationFn: async (data: Education["Update"]) => {
			return await updateEducation({ ...data, education_id: educationId });
		},
		onError: () => {
			toast.error("Error updating education");
		},
		onSuccess: () => {
			toast.success("Education saved");
		},
		onSettled: () => {
			invalidateById(diaryId);
		},
	});
};
